import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getUserCreds, host } from "../utils/utils"
import { SAFE_PAGE_FRAME } from "@/utils/safeArea"
import backgroundImage from "@/images/backgrounds/lobby-background.webp"
import Navbar from "../Components/Navbar"
import avatar from "@/images/avatar.webp"
import avatar0 from "@/images/avatars/avatar0.webp"
import avatar1 from "@/images/avatars/avatar1.webp"
import avatar2 from "@/images/avatars/avatar2.webp"
import avatar3 from "@/images/avatars/avatar3.webp"
import avatar4 from "@/images/avatars/avatar4.webp"
import avatar5 from "@/images/avatars/avatar5.webp"
import avatar6 from "@/images/avatars/avatar6.webp"
import avatar7 from "@/images/avatars/avatar7.webp"
import PiecePicker from "@/Components/PiecePicker"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { addToastNotification, setState } from "@/store/globalSlice"
import { googleSignOut, suppressGoogleAutoSignIn } from "@/utils/googleAuth"
import { Panel } from "./Stats/statsUi"

interface ProfileSettingsProps {}

// Same bounds the sign-up form enforces, so a name that was valid on the way in
// can't be rejected on the way back out.
const MIN_NAME_LENGTH = 3
const MAX_NAME_LENGTH = 20
// Uploads go straight into object storage, so this is the one place to keep a
// 12MP phone photo from being pushed through as a profile picture.
const MAX_PHOTO_BYTES = 5 * 1024 * 1024
// Deleting an account cannot be walked back, so the confirm dialog asks for
// this to be typed out rather than settling for one more click in the same
// spot the last one was.
const DELETE_CONFIRM_PHRASE = "DELETE"

const ProfileSettings: React.FC<ProfileSettingsProps> = () => {
  const nav = useNavigate()
  const dispatch = useAppDispatch()
  // The account as the server last told us it is. Everything on this page is
  // diffed against it, so the save bar only appears once something really moved.
  const userData = useAppSelector((state) => state.global.userData)

  const [displayName, setDisplayName] = useState("")
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null)
  const [photoPreview, setPhotoPreview] = useState(avatar)
  const [selectedAvatar, setSelectedAvatar] = useState(-1)
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showNameError, setShowNameError] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")
  const [isDeleting, setIsDeleting] = useState(false)

  const avatars = [
    avatar0,
    avatar1,
    avatar2,
    avatar3,
    avatar4,
    avatar5,
    avatar6,
    avatar7,
  ]

  const savedName = userData?.username ?? ""
  const savedPhoto = userData?.profileImage || avatar

  // Object URLs for uploaded files have to be released by hand, and the picture
  // can be swapped several times before anyone hits save.
  const objectUrlRef = useRef<string | null>(null)
  const showPreview = (url: string) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    objectUrlRef.current = url.startsWith("blob:") ? url : null
    setPhotoPreview(url)
  }
  useEffect(
    () => () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
    },
    []
  )

  // Fill the form with what the account already has, once. It's a settings
  // page, not a sign-up form: an empty name field would read as "no name set",
  // and saving one would be rejected by the server anyway. Guarded by a ref so
  // a later refresh of the account data can't overwrite what's being typed.
  const hasPrefilled = useRef(false)
  useEffect(() => {
    if (hasPrefilled.current || !userData?.email) return
    hasPrefilled.current = true
    setDisplayName(userData.username ?? "")
    if (userData.profileImage) showPreview(userData.profileImage)
  }, [userData])

  const toast = (
    message: string,
    messageType: "success" | "error" | "info" = "info"
  ) => {
    dispatch(addToastNotification({ message, messageType, type: "general" }))
  }

  const trimmedName = displayName.trim()
  const nameError =
    trimmedName.length === 0
      ? "Pick a display name — this is what other players see."
      : trimmedName.length < MIN_NAME_LENGTH
      ? `At least ${MIN_NAME_LENGTH} characters.`
      : null
  const pictureChanged = !!profilePhoto || selectedAvatar >= 0
  const hasChanges = trimmedName !== savedName || pictureChanged
  const canSave = hasChanges && !nameError && !isSaving

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Re-selecting the same file has to fire the change event again, so the
    // input is cleared on every pass.
    e.target.value = ""
    if (!file) return
    if (!file.type.startsWith("image/")) {
      toast("That file isn't an image.", "error")
      return
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast("That image is over 5MB — pick a smaller one.", "error")
      return
    }
    setProfilePhoto(file)
    showPreview(URL.createObjectURL(file))
    setSelectedAvatar(-1) // Clear avatar selection when custom photo is uploaded
  }

  const handleAvatarSelect = (index: number) => {
    setSelectedAvatar(index)
    setProfilePhoto(null)
    showPreview(avatars[index])
  }

  // Puts the picture back the way the account has it. Used by both the dialog's
  // Cancel and the page-level Discard, so "cancel" always means cancel.
  const revertPicture = () => {
    setProfilePhoto(null)
    setSelectedAvatar(-1)
    showPreview(savedPhoto)
  }

  // Escape closes the picture dialog the same way Cancel does.
  useEffect(() => {
    if (!isProfileDialogOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      revertPicture()
      setIsProfileDialogOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isProfileDialogOpen, savedPhoto])

  const closeDeleteDialog = () => {
    // Nothing closes the dialog once the request is in flight — the account is
    // already being taken apart server-side, and a closed dialog would leave
    // the page looking as though nothing had happened.
    if (isDeleting) return
    setIsDeleteDialogOpen(false)
    setDeleteConfirmText("")
  }

  // Escape backs out of the delete dialog, same as its Cancel.
  useEffect(() => {
    if (!isDeleteDialogOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDeleteDialog()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isDeleteDialogOpen, isDeleting])

  const canDelete =
    deleteConfirmText.trim().toUpperCase() === DELETE_CONFIRM_PHRASE &&
    !isDeleting

  // Erases the account for good. The server works out whose account it is from
  // the token rather than from anything sent in the body, so this can only ever
  // delete the account that is signed in here.
  const handleDeleteAccount = async () => {
    if (!canDelete) return
    setIsDeleting(true)
    try {
      const res = await fetch(`${host}/api/account`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getUserCreds().token}` },
      })
      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        toast(data.error || "Couldn't delete your account.", "error")
        setIsDeleting(false)
        return
      }

      // The same teardown a log out does (see Navbar), because from here on
      // there is no account left to sign back into.
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      suppressGoogleAutoSignIn()
      void googleSignOut()
      dispatch(setState({ isLoggedIn: false, userData: undefined }))
      toast("Your account and its data have been deleted.", "success")
      // Replace, so the back button can't land them on a settings page for an
      // account that no longer exists.
      nav("/login", { replace: true })
    } catch (error) {
      console.error("Error deleting account:", error)
      toast(
        "Couldn't reach the server — your account wasn't deleted.",
        "error"
      )
      setIsDeleting(false)
    }
  }

  const discardChanges = () => {
    setDisplayName(savedName)
    setShowNameError(false)
    revertPicture()
  }

  const handleSubmit = async () => {
    if (nameError) {
      setShowNameError(true)
      return
    }
    if (!hasChanges) return
    setIsSaving(true)

    try {
      const email = getUserCreds().email
      const formData = new FormData()
      formData.append("username", trimmedName)
      formData.append("email", email)

      // Handle profile image - either custom photo or selected avatar
      if (profilePhoto) {
        // Custom uploaded photo
        formData.append("profileImage", profilePhoto)
      } else if (selectedAvatar >= 0) {
        // Selected avatar - convert to file
        try {
          const avatarUrl = avatars[selectedAvatar]
          const response = await fetch(avatarUrl)
          const blob = await response.blob()
          // The built-in avatars are WebP. Calling them image/png stored the
          // wrong Content-Type on the object, which only ever rendered because
          // browsers sniff the bytes of an <img> and ignore the header.
          const type = blob.type || "image/webp"
          const avatarFile = new File([blob], `avatar${selectedAvatar}.webp`, {
            type,
          })
          formData.append("profileImage", avatarFile)
        } catch (avatarError) {
          // Saving the name alone here would clear the "unsaved changes" bar
          // while quietly dropping the picture, so the whole save is called off.
          console.error("Failed to convert avatar to file:", avatarError)
          toast("Couldn't read that avatar — nothing was saved.", "error")
          return // the finally below clears the saving state
        }
      }

      const res = await fetch(`${host}/api/profile`, {
        method: "POST",
        body: formData,
      })
      const data = await res.json()

      if (res.ok) {
        const savedUser = data.user
        // No cache-busting needed on the way back: every upload lands on its
        // own storage key, so a changed picture comes back as a URL nobody has
        // fetched before and every client loads it without being nudged.
        // The saved account goes back into the store rather than reloading the
        // page: the navbar reads from there, so the new name and picture show
        // up everywhere immediately and the success toast survives.
        dispatch(setState({ userData: savedUser }))
        setProfilePhoto(null)
        setSelectedAvatar(-1)
        if (savedUser?.profileImage) showPreview(savedUser.profileImage)
        toast("Profile saved.", "success")
      } else {
        toast(data.error || "Couldn't save your profile.", "error")
      }
    } catch (error) {
      console.error("Error updating profile:", error)
      toast("Couldn't reach the server — your profile wasn't saved.", "error")
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      className="relative w-screen h-screen bg-cover flex flex-col"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/80 via-black/70 to-black/90" />
      <Navbar />

      {/* The page scrolls here rather than on the window: the app pins
          html/body at 100% with overflow hidden, so a taller-than-viewport
          settings form is simply unreachable without its own scroller. */}
      <div className={`relative z-10 flex-1 min-h-0 overflow-y-auto ${SAFE_PAGE_FRAME}`}>
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-4 sm:gap-2.5 pb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => nav(-1)}
              title="Go back"
              className="flex h-10 w-10 mobile:h-[34px] mobile:w-[34px] shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/40 mobile:text-[14px]/[1.4] text-white/70 transition-colors hover:border-white/20 hover:bg-black/60 hover:text-white"
            >
              <i className="fa-solid fa-arrow-left" />
            </button>
            <div className="min-w-0">
              <h1 className="font-fourth text-2xl mobile:text-[20px]/[1.15] font-bold tracking-wide text-white sm:text-xl">
                Profile Settings
              </h1>
              <p className="font-poppins text-sm mobile:text-[12px]/[1.4] text-white/40 sm:text-xs">
                How you show up at the table.
              </p>
            </div>
          </div>

          {/* Identity */}
          <div
            className="relative overflow-hidden rounded-3xl border border-white/10 bg-black/50 p-6 sm:p-4 mobile:p-[14px]"
            style={{ boxShadow: "inset 0 0 120px -40px rgba(251,191,36,0.4)" }}
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber-400/20 blur-3xl" />
            <div className="relative flex flex-wrap items-center gap-6 sm:gap-3 mobile:gap-[14px]">
              <button
                onClick={() => setIsProfileDialogOpen(true)}
                title="Change your picture"
                className="group relative h-28 w-28 shrink-0 rounded-full border-2 border-amber-400 p-1 transition-transform hover:scale-[1.03] sm:h-20 sm:w-20 mobile:h-[72px] mobile:w-[72px]"
              >
                <img
                  src={photoPreview}
                  alt="Profile"
                  className="h-full w-full rounded-full object-cover"
                />
                <span className="absolute inset-1 flex items-center justify-center rounded-full bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                  <i className="fa-solid fa-camera text-xl mobile:text-[17px]/[1.25] text-white" />
                </span>
                <span className="absolute -bottom-1 left-1/2 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border-2 border-black/70 bg-amber-400 sm:h-6 sm:w-6 mobile:h-[22px] mobile:w-[22px]">
                  <i className="fa-solid fa-pen text-xs mobile:text-[11px]/[1.4] text-black/80" />
                </span>
              </button>

              <div className="min-w-0 flex-1">
                <p className="truncate font-fourth text-3xl mobile:text-[22px]/[1.15] font-bold text-white sm:text-xl">
                  {trimmedName || savedName || "Player"}
                </p>
                <p className="mt-1 truncate font-poppins text-sm mobile:text-[12px]/[1.4] text-white/40">
                  {userData?.email || getUserCreds().email}
                </p>
                <button
                  onClick={() => setIsProfileDialogOpen(true)}
                  className="mt-3 inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 mobile:px-[10px] mobile:py-[7px] font-poppins text-xs mobile:text-[11px]/[1.4] font-semibold text-white/80 transition-colors hover:border-white/20 hover:bg-white/10 hover:text-white"
                >
                  <i className="fa-solid fa-image" />
                  Change picture
                </button>
              </div>
            </div>
          </div>

          {/* Display name */}
          <Panel
            title="Display name"
            subtitle="Shown on your seat, in chat and on the leaderboard"
            icon="fa-id-badge"
          >
            <input
              value={displayName}
              maxLength={MAX_NAME_LENGTH}
              onChange={(e) => setDisplayName(e.target.value)}
              onBlur={() => setShowNameError(true)}
              placeholder="Your name at the table"
              data-testid="profile-settings.display-name"
              className={`w-full rounded-xl border bg-black/40 px-4 py-3 mobile:px-[12px] mobile:py-[9px] font-poppins mobile:text-[16px]/[1.25] text-white placeholder-white/30 outline-none transition-colors ${
                showNameError && nameError
                  ? "border-rose-500/50 focus:border-rose-400"
                  : "border-white/10 focus:border-amber-400"
              }`}
            />
            <div className="mt-2 flex items-start justify-between gap-3">
              <p
                className={`font-poppins text-xs mobile:text-[11px]/[1.4] ${
                  showNameError && nameError ? "text-rose-300" : "text-white/30"
                }`}
              >
                {showNameError && nameError
                  ? nameError
                  : `${MIN_NAME_LENGTH}–${MAX_NAME_LENGTH} characters.`}
              </p>
              <span className="shrink-0 font-poppins text-xs mobile:text-[11px]/[1.4] tabular-nums text-white/30">
                {displayName.length}/{MAX_NAME_LENGTH}
              </span>
            </div>
          </Panel>

          {/* Game piece. Saved on the account like the name and picture,
              but kept out of the Save Changes flow below: it applies (and
              saves) the moment it's clicked, so a game already running
              changes over immediately. */}
          <Panel
            title="Game piece"
            subtitle="Applies the moment you pick it — no save needed"
            icon="fa-chess-knight"
          >
            <PiecePicker
              className="!grid-cols-3 md:!grid-cols-6"
              testIdPrefix="profile-settings"
            />
          </Panel>

          {/* Danger zone. Both app stores require an in-app route to account
              deletion — it can't be a mail-support-and-wait affair — so it
              lives here, on the page people already come to for their account.
              Kept out of the Save Changes flow: it is not a change you save. */}
          <Panel
            title="Danger zone"
            subtitle="Permanent — there is no undo"
            icon="fa-triangle-exclamation"
            accent="#FB7185"
            className="!border-rose-500/30"
          >
            <div className="flex flex-wrap items-center justify-between gap-4 mobile:gap-[10px]">
              <p className="min-w-[200px] flex-1 font-poppins text-xs mobile:text-[11px]/[1.4] text-white/40">
                Deleting your account wipes your profile, your picture, your
                stats and rating, and takes you off everyone's friends list.
                Games you have already played stay in the other players'
                history.
              </p>
              <button
                onClick={() => setIsDeleteDialogOpen(true)}
                data-testid="profile-settings.delete-account"
                className="flex shrink-0 items-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-2.5 mobile:px-[12px] mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-semibold text-rose-300 transition-colors hover:border-rose-400 hover:bg-rose-500/20 hover:text-rose-200"
              >
                <i className="fa-solid fa-trash-can" />
                Delete account
              </button>
            </div>
          </Panel>

          {/* Save bar. Sticks to the bottom of the scroller so it stays in
              reach however far down the page you are. */}
          <div className="sticky bottom-0 -mx-1 mt-1 rounded-2xl border border-white/10 bg-black/80 px-5 py-4 backdrop-blur-md sm:px-3 sm:py-2.5 mobile:px-[12px] mobile:py-[10px]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="font-poppins text-xs mobile:text-[11px]/[1.4] text-white/40">
                {hasChanges ? (
                  <span className="flex items-center gap-2 text-amber-300">
                    <i className="fa-solid fa-circle text-[0.4rem] mobile:text-[6px]/[1.4]" />
                    Unsaved changes
                  </span>
                ) : (
                  "Everything is saved."
                )}
              </p>
              <div className="flex items-center gap-3">
                {hasChanges && (
                  <button
                    onClick={discardChanges}
                    disabled={isSaving}
                    className="rounded-xl px-4 py-2.5 mobile:px-[12px] mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-semibold text-white/50 transition-colors hover:text-white disabled:opacity-50"
                  >
                    Discard
                  </button>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={!canSave}
                  data-testid="profile-settings.save"
                  className={`flex items-center gap-2 rounded-xl px-6 py-2.5 mobile:px-[18px] mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-bold transition-all ${
                    canSave
                      ? "bg-amber-400 text-black hover:bg-amber-300"
                      : "cursor-not-allowed bg-white/10 text-white/30"
                  }`}
                >
                  {isSaving && <i className="fa-solid fa-circle-notch fa-spin" />}
                  {isSaving ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation */}
      {isDeleteDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
          onClick={closeDeleteDialog}
        >
          <div
            className="max-h-full w-full max-w-md mobile:max-w-[360px] overflow-y-auto rounded-3xl border border-rose-500/30 bg-[#12100e] p-6 shadow-2xl sm:p-4 mobile:p-[14px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mobile:gap-[10px]">
              <span className="flex h-10 w-10 mobile:h-[32px] mobile:w-[32px] shrink-0 items-center justify-center rounded-full bg-rose-500/20 text-rose-300">
                <i className="fa-solid fa-triangle-exclamation mobile:text-[13px]/[1.4]" />
              </span>
              <div className="min-w-0">
                <h3 className="font-fourth text-xl mobile:text-[17px]/[1.25] font-bold tracking-wide text-white">
                  Delete your account?
                </h3>
                <p className="font-poppins text-xs mobile:text-[11px]/[1.4] text-white/40">
                  This can't be undone, and the account can't be recovered.
                </p>
              </div>
            </div>

            <ul className="mt-4 mobile:mt-[12px] space-y-2 mobile:space-y-[6px] rounded-2xl border border-white/10 bg-black/40 p-4 mobile:p-[12px] font-poppins text-xs mobile:text-[11px]/[1.4] text-white/60">
              {[
                "Your name, email and profile picture",
                "Your stats, rating and match history",
                "Your friends list and pending invites",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <i className="fa-solid fa-xmark mt-0.5 shrink-0 text-rose-400" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 mobile:mt-[12px]">
              <label
                htmlFor="delete-confirm-input"
                className="block font-poppins text-xs mobile:text-[11px]/[1.4] text-white/40"
              >
                Type{" "}
                <span className="font-bold text-rose-300">
                  {DELETE_CONFIRM_PHRASE}
                </span>{" "}
                to confirm
              </label>
              <input
                id="delete-confirm-input"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                disabled={isDeleting}
                autoComplete="off"
                placeholder={DELETE_CONFIRM_PHRASE}
                data-testid="profile-settings.delete-confirm-input"
                className="mt-2 mobile:mt-[6px] w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 mobile:px-[12px] mobile:py-[9px] font-poppins mobile:text-[16px]/[1.25] tracking-wider text-white placeholder-white/20 outline-none transition-colors focus:border-rose-400 disabled:opacity-50"
              />
            </div>

            <div className="mt-5 mobile:mt-[14px] flex gap-3 mobile:gap-[8px]">
              <button
                onClick={closeDeleteDialog}
                disabled={isDeleting}
                className="flex-1 rounded-xl border border-white/10 px-4 py-2.5 mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-semibold text-white/60 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-50"
              >
                Keep my account
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={!canDelete}
                data-testid="profile-settings.delete-confirm"
                className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-bold transition-all ${
                  canDelete
                    ? "bg-rose-500 text-white hover:bg-rose-400"
                    : "cursor-not-allowed bg-white/10 text-white/30"
                }`}
              >
                {isDeleting && (
                  <i className="fa-solid fa-circle-notch fa-spin" />
                )}
                {isDeleting ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Image Selection Dialog */}
      {isProfileDialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
          onClick={() => {
            revertPicture()
            setIsProfileDialogOpen(false)
          }}
        >
          <div
            className="max-h-full w-full max-w-md mobile:max-w-[360px] overflow-y-auto rounded-3xl border border-white/10 bg-[#12100e] p-6 shadow-2xl sm:p-4 mobile:p-[14px]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 mobile:mb-[12px] flex items-start justify-between gap-4">
              <div>
                <h3 className="font-fourth text-xl mobile:text-[17px]/[1.25] font-bold tracking-wide text-white">
                  Your picture
                </h3>
                <p className="font-poppins text-xs mobile:text-[11px]/[1.4] text-white/40">
                  Upload your own, or take one of ours.
                </p>
              </div>
              <button
                onClick={() => {
                  revertPicture()
                  setIsProfileDialogOpen(false)
                }}
                title="Close"
                className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-lg text-[20px] text-white/40 transition-colors hover:bg-white/10 hover:text-white"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="space-y-5 mobile:space-y-[14px]">
              {/* Upload */}
              <button
                onClick={() => document.getElementById("photo-input")?.click()}
                className="group mx-auto flex flex-col items-center gap-3"
              >
                <div className="relative h-32 w-32 rounded-full border-2 border-amber-400 p-1 transition-transform group-hover:scale-[1.03] sm:h-24 sm:w-24 mobile:h-[84px] mobile:w-[84px]">
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="h-full w-full rounded-full object-cover"
                  />
                  <span className="absolute inset-1 flex items-center justify-center rounded-full bg-black/60 opacity-0 transition-opacity group-hover:opacity-100">
                    <i className="fa-solid fa-arrow-up-from-bracket text-xl mobile:text-[17px]/[1.25] text-white" />
                  </span>
                </div>
                <span className="font-poppins text-xs mobile:text-[11px]/[1.4] font-semibold text-white/60 group-hover:text-white">
                  Upload a photo — up to 5MB
                </span>
              </button>
              <input
                id="photo-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
              />

              {/* Avatar Selection Grid */}
              <div>
                <div className="mb-3 flex items-center gap-3">
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="font-poppins text-[0.65rem] mobile:text-[10px]/[1.4] font-semibold uppercase tracking-wider text-white/40">
                    or pick an avatar
                  </span>
                  <span className="h-px flex-1 bg-white/10" />
                </div>
                <div className="grid grid-cols-4 gap-3 mobile:gap-[8px]">
                  {avatars.map((avatarOption, index) => (
                    <button
                      key={index}
                      onClick={() => handleAvatarSelect(index)}
                      title={`Avatar ${index + 1}`}
                      className={`relative aspect-square overflow-hidden rounded-full border-2 transition-all ${
                        selectedAvatar === index && !profilePhoto
                          ? "border-amber-400 ring-2 ring-amber-400/50"
                          : "border-white/10 hover:border-white/40"
                      }`}
                    >
                      <img
                        src={avatarOption}
                        alt={`Avatar ${index}`}
                        className="h-full w-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => {
                    revertPicture()
                    setIsProfileDialogOpen(false)
                  }}
                  className="flex-1 rounded-xl border border-white/10 px-4 py-2.5 mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-semibold text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setIsProfileDialogOpen(false)}
                  className="flex-1 rounded-xl bg-amber-400 px-4 py-2.5 mobile:py-[9px] font-poppins text-sm mobile:text-[12px]/[1.4] font-bold text-black transition-colors hover:bg-amber-300"
                >
                  Use this picture
                </button>
              </div>
              <p className="text-center font-poppins text-[0.65rem] mobile:text-[10px]/[1.4] text-white/30">
                Your picture isn't saved until you hit Save changes.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfileSettings
